import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import { OTPInput } from "@/elements/components/ui/otp-input";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useState, useEffect } from "react";
import { ScrollView, Text, View } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthActions } from "@convex-dev/auth/react";
import { handleAuthError } from "@/elements/components/utils/auth-error-handler";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { auth } from "@/elements/components/utils/convex-api";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import Toast from "react-native-toast-message";

const VerifyResetCodeSchema = z.object({
  code: z.string().length(8, "Code must be 8 digits"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
});

type VerifyResetCodeSchemaType = z.infer<typeof VerifyResetCodeSchema>;

function VerifyResetCodeScreen() {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm({
    defaultValues: {
      code: "",
      newPassword: "",
    },
    mode: "onChange",
    resolver: zodResolver(VerifyResetCodeSchema),
  });
  const { signIn } = useAuthActions();
  const { email, persona } = useLocalSearchParams<{
    email?: string;
    persona?: "parent" | "tutor" | "student";
  }>();

  const { data: isAuthenticated = false, isSuccess } = useQuery(
    convexQuery(auth.isAuthenticated, {})
  );

  if (!email || !persona) {
    return <Redirect href={"/(auth)/sign-in-options"} />;
  }

  if (!isSuccess) {
    return <Loader />;
  }

  if (isAuthenticated) {
    return <Redirect href={"/(app)/home"} />;
  }

  const handleOTPComplete = (code: string) => {
    setOtpCode(code);
    setValue("code", code);
    setOtpError(false);
  };

  const handleOTPChange = (code: string) => {
    setOtpCode(code);
    setValue("code", code);
    if (code.length < 8) {
      setOtpError(false);
    }
  };

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendCode = async () => {
    if (resendCooldown > 0 || isResending) return;
    
    try {
      setIsResending(true);
      await signIn("password", {
        email,
        flow: "reset",
      });
      setResendCooldown(60); // 60 second cooldown
      Toast.show({
        type: "success",
        text1: "New code sent to your email",
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    } catch (error) {
      const errorResult = handleAuthError(error);
      Toast.show({
        type: "error",
        text1: errorResult.message,
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    } finally {
      setIsResending(false);
    }
  };

  const onSubmit = async (data: VerifyResetCodeSchemaType) => {
    try {
      setIsSubmitting(true);
      console.log("Submitting password reset with:", {
        email,
        code: data.code,
        codeLength: data.code.length,
        newPassword: "***hidden***",
        flow: "reset-verification",
      });
      await signIn("password", {
        email,
        code: data.code,
        newPassword: data.newPassword,
        flow: "reset-verification",
      });
      
      Toast.show({
        type: "success",
        text1: "Password reset successfully",
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
      
      router.replace({
        pathname: "/(auth)/sign-in",
        params: { persona },
      });
    } catch (error) {
      const errorResult = handleAuthError(error);
      console.log(errorResult);
      
      if (errorResult.message.includes("code") || errorResult.message.includes("token")) {
        setOtpError(true);
      }
      
      Toast.show({
        type: "error",
        text1: errorResult.message,
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasPasswordError = errors.newPassword;

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon
          onPress={() =>
            router.canGoBack()
              ? router.back()
              : router.replace({
                  pathname: "/(auth)/forgot-password",
                  params: { persona },
                })
          }
        />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Check your email
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-black leading-[1.5] ">
                Enter the 8-digit code sent to your email and choose a new password
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-6">
              <View className="gap-2">
                <OTPInput
                  length={8}
                  onComplete={handleOTPComplete}
                  onChangeText={handleOTPChange}
                  error={otpError}
                />
                {otpError && (
                  <Text className="font-geologica-regular font-normal text-[14px] text-red-500 mx-4">
                    Invalid verification code. Please try again.
                  </Text>
                )}
                <View className="items-center mt-2">
                  <Text
                    onPress={handleResendCode}
                    className={`font-geologica-medium font-medium text-[16px] ${
                      resendCooldown > 0 || isResending
                        ? 'text-gray-400'
                        : 'text-teal-600'
                    }`}
                  >
                    {resendCooldown > 0 
                      ? `Resend code in ${resendCooldown}s` 
                      : isResending 
                      ? "Sending..." 
                      : "Or send a new code"
                    }
                  </Text>
                </View>
              </View>
              
              <View className="gap-2">
                <Controller
                  control={control}
                  name="newPassword"
                  render={({ field, fieldState }) => (
                    <Input
                      size="md"
                      placeholder="New password"
                      {...field}
                      secureTextEntry
                      error={!!fieldState.error?.message}
                    />
                  )}
                />
                <Text 
                  className={`font-geologica-regular font-normal text-[14px] leading-[1.5] mx-4 ${
                    hasPasswordError ? 'text-red-500' : 'text-gray-400'
                  }`}
                >
                  Password must have atleast 8 characters with one capital letter and one number or symbol.
                </Text>
              </View>
              
              <Button
                className="w-full"
                size="md"
                intent="primary"
                onPress={handleSubmit(onSubmit)}
                disabled={!isValid || isSubmitting || otpCode.length < 8}
              >
                <Button.Text size="md">Sign me in</Button.Text>
              </Button>
            </View>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default VerifyResetCodeScreen;